import React from 'react';

import useClassy from '@core/hooks/useClassy';

import Button from '@ui/Button';
import Icon from '@ui/Icon';
import Menu, { MenuItem } from '@ui/Menu';

import styles from './AIDropdownPreview.module.scss';

interface AIDropdownPreviewProps {
  enableChatGPT: boolean;
  enableClaude: boolean;
  enableClipboard: boolean;
  enableViewAsMarkdown: boolean;
}

/**
 * A component that shows the AI dropdown button with its menu expanded
 * This is used for the preview in the settings panel
 */
const ExpandedAIDropdown: React.FC<AIDropdownPreviewProps> = ({
  enableChatGPT,
  enableClaude,
  enableClipboard,
  enableViewAsMarkdown,
}) => {
  const bem = useClassy(styles, 'AIDropdownPreview');

  // Check if any options are enabled
  const hasEnabledOptions = enableChatGPT || enableClaude || enableClipboard || enableViewAsMarkdown;

  // If no options are enabled, show a message
  if (!hasEnabledOptions) {
    return (
      <div className={bem('&', 'empty')}>
        <p className={bem('-empty-message')}>Enable at least one AI option to see the dropdown</p>
      </div>
    );
  }

  return (
    <div className={bem('-expanded')}>
      <div className={bem('-trigger')}>
        <Button kind="secondary" outline size="sm">
          <Icon name="sparkles" /> Ask AI <Icon name="chevron-down" />
        </Button>
      </div>
      <div className={bem('-menu')}>
        <Menu>
          {!!enableChatGPT && (
            <MenuItem>
              <Icon name="openai" /> Ask ChatGPT
            </MenuItem>
          )}
          {!!enableClaude && (
            <MenuItem>
              <Icon name="claude" /> Ask Claude
            </MenuItem>
          )}
          {!!enableClipboard && (
            <MenuItem>
              <Icon name="clipboard" /> Copy to Clipboard
            </MenuItem>
          )}
          {!!enableViewAsMarkdown && (
            <MenuItem>
              <Icon name="text" /> View as Markdown
            </MenuItem>
          )}
        </Menu>
      </div>
    </div>
  );
};

/**
 * A component that renders a live preview of the AIDropdown component
 * Used in the AI settings panel to show how the dropdown will look with current settings
 */
const AIDropdownPreview: React.FC<AIDropdownPreviewProps> = ({
  enableChatGPT,
  enableClaude,
  enableClipboard,
  enableViewAsMarkdown,
}) => {
  // We're now using the ExpandedAIDropdown instead of the regular AIDropdown
  return (
    <ExpandedAIDropdown
      enableChatGPT={enableChatGPT}
      enableClaude={enableClaude}
      enableClipboard={enableClipboard}
      enableViewAsMarkdown={enableViewAsMarkdown}
    />
  );
};

export default AIDropdownPreview;
